import { Badge, BadgeLevel, BadgeShape, BadgeSize, Button, ButtonLevel, ButtonSize, ImpactBolt } from '@vizit/atoms';
import { Text, TextColor, TextVariant, Tooltip, TooltipTrigger, WeightIndicator, useHover } from '@vizit/atoms';
import { ArrowCompactDownIcon, ArrowCompactUpIcon, InfoCircleIcon } from '@vizit/icons';
import { cx } from '@vizit/theme';
import * as React from 'react';
import { mergeProps } from 'react-aria';
import { TooltipTriggerStateContext } from 'react-aria-components';
import { MetricCardSkeleton } from '../metric-card-skeleton';
import { containerClasses, getScoreColor, getScoreColorRange } from './styles';
import type { ScoreMetricCellProps } from './types';

/**
 * Opens the info tip while the cell is hovered by driving react-aria's own tooltip state
 * rather than forcing `isOpen`, so the open delay, Escape to dismiss, and the
 * one-tooltip-at-a-time registry all keep working. Renders nothing.
 */
function CellHoverTooltip({ isHovered }: { isHovered: boolean }) {
  const state = React.useContext(TooltipTriggerStateContext);
  // The state object is new on every render; reading it through a ref keeps the effect keyed
  // to hover changes alone, so a re-render after Escape doesn't reopen the tip.
  // Synced in an effect rather than during render; declared first so it runs before the hover effect below.
  const stateRef = React.useRef(state);
  React.useEffect(() => {
    stateRef.current = state;
  });
  // Only close what hover opened, so leaving the cell doesn't close a tip opened by keyboard focus.
  const openedByHover = React.useRef(false);

  React.useEffect(() => {
    if (isHovered) {
      // A tip already open (from keyboard focus on the info button) stays focus-owned.
      if (!stateRef.current?.isOpen) openedByHover.current = true;
      stateRef.current?.open();
    } else if (openedByHover.current) {
      openedByHover.current = false;
      stateRef.current?.close(true);
    }
  }, [isHovered]);

  return null;
}

/**
 * Generic score metric cell displaying a score out of 100
 * with impact bolts and an optional score diff badge.
 * Score number color is based on score range:
 * 0-39 = red, 40-59 = orange, 60-79 = yellow, 80-100 = green.
 *
 * @example
 * <ScoreMetricCell label="CAROUSEL SCORE" score={32} impactLevel="high" />
 * <ScoreMetricCell label="HERO ASSET SCORE" score={32} impactLevel="medium" scoreDiff={5} />
 */
const ScoreMetricCell = React.forwardRef<HTMLDivElement, ScoreMetricCellProps>((props, forwardedRef) => {
  const {
    label,
    score,
    impactLevel,
    scoreDiff,
    isDisabled = false,
    isFocused = false,
    isLoading = false,
    weight,
    notes,
    infoTooltip,
    action,
    reserveActionSpace = false,
    isScoreMuted = false,
    className,
    ...other
  } = props;

  // The info tip opens while the pointer is anywhere on the cell, except over the weight bolt,
  // which shows its own tooltip instead. Focus/hover on the info icon itself still opens it.
  // useHover ignores touch, so a tap doesn't flash the tip or the notes color.
  const { hoverProps: cellHoverProps, isHovered: isCellHovered } = useHover({});
  const { hoverProps: boltHoverProps, isHovered: isBoltHovered } = useHover({});
  // Merged onto the skeleton too, so leaving the cell while it's loading still ends the hover.
  const rootProps = mergeProps(other, cellHoverProps);

  if (isLoading) {
    return (
      <div
        ref={forwardedRef}
        className={cx(containerClasses({ state: 'default' }), className)}
        aria-busy="true"
        data-testid="score-metric-cell"
        {...rootProps}
      >
        <MetricCardSkeleton />
      </div>
    );
  }

  const hasDiff = scoreDiff != null && scoreDiff !== 0;
  const isPositiveDiff = hasDiff && scoreDiff > 0;
  const colorRange = getScoreColorRange(score);
  const state = isDisabled ? 'disabled' : isFocused ? 'focused' : 'default';

  return (
    <div
      ref={forwardedRef}
      className={cx(containerClasses({ state }), className)}
      data-testid="score-metric-cell"
      data-state={state}
      {...rootProps}
    >
      <div className="flex items-start justify-between w-full">
        <div className="flex items-center gap-1">
          <Text
            variant={TextVariant.MonoXs}
            color={TextColor.Quaternary}
            className={isDisabled ? 'text-fg-disabled-subtle' : ''}
            showEllipsis={false}
          >
            {label}
          </Text>
          {infoTooltip != null && (
            <TooltipTrigger delay={200}>
              <CellHoverTooltip isHovered={isCellHovered && !isBoltHovered} />
              <Button
                aria-label={`${label} info`}
                level={ButtonLevel.Ghost}
                size={ButtonSize.Xs}
                className="cursor-default p-0 h-auto min-h-0"
              >
                <InfoCircleIcon size={12} className={isDisabled ? 'text-fg-disabled-subtle' : 'text-quaternary'} />
              </Button>
              <Tooltip>{infoTooltip}</Tooltip>
            </TooltipTrigger>
          )}
        </div>
        <span className="inline-flex" {...boltHoverProps}>
          {weight != null ? (
            <WeightIndicator
              pct={weight.pct}
              accentClassName={weight.accentClassName}
              size={weight.size}
              isDisabled={isDisabled}
            />
          ) : (
            <ImpactBolt level={impactLevel} hideUnfilled isDisabled={isDisabled} />
          )}
        </span>
      </div>

      <div className="flex items-center justify-between w-full gap-2">
        <div className="flex items-center gap-2">
          <div className="flex flex-col gap-0.5">
            <Text
              variant={TextVariant.MonoXl}
              elementType="span"
              color={TextColor.Inherit}
              className={
                // Disabled (a sibling cell or step card is hovered) always drops to the
                // disabled gray so the focused cell pops; otherwise with the weights panel
                // open every non-isolated score mutes to tertiary.
                isDisabled ? 'text-fg-disabled-subtle' : isScoreMuted ? 'text-tertiary' : getScoreColor(colorRange)
              }
              showEllipsis={false}
              data-testid="score-value"
              data-muted={isScoreMuted ? 'true' : 'false'}
            >
              {score}
            </Text>
            <Text
              variant={TextVariant.MonoSm}
              color={TextColor.Tertiary}
              className={isDisabled ? 'text-fg-disabled-subtle' : ''}
              showEllipsis={false}
            >
              /100
            </Text>
          </div>
          {hasDiff && (
            <div className={isDisabled ? 'opacity-20' : ''}>
              <Badge
                level={isPositiveDiff ? BadgeLevel.Success : BadgeLevel.Error}
                size={BadgeSize.Xs}
                shape={BadgeShape.Box}
                bordered={false}
              >
                {isPositiveDiff ? <ArrowCompactUpIcon size={8} /> : <ArrowCompactDownIcon size={8} />}
                {Math.abs(scoreDiff)}
              </Badge>
            </div>
          )}
        </div>
        {(notes != null || action != null || reserveActionSpace) && (
          <div className="flex flex-col items-end gap-3">
            {notes != null && (
              <Text
                variant={TextVariant.XsRegular}
                color={TextColor.Inherit}
                elementType="span"
                showEllipsis={false}
                className={cx(
                  'text-right max-w-[94px] text-quinary [&_strong]:font-semibold transition-colors duration-150',
                  isCellHovered && 'text-quinary-hover',
                  isFocused && 'text-tertiary',
                  isDisabled && 'text-fg-disabled-subtle',
                )}
                data-testid="score-metric-notes"
              >
                {notes}
              </Text>
            )}
            {action ?? (reserveActionSpace && <div aria-hidden="true" className="h-14" />)}
          </div>
        )}
      </div>
    </div>
  );
});

ScoreMetricCell.displayName = 'ScoreMetricCell';

export { ScoreMetricCell };
export type { ScoreMetricCellProps };
